import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../utils/config';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import './ProductSearchModal.css';

interface SupplierProduct {
  _id: string;
  supplier_id: string;
  specific_id: string;
  product_name: string;
  product_brand: string;
  product_global_code: string;
  product_company_code: string;
  product_description: string;
  product_category: string;
  price: number;
  currency: string;
  delivery_time: string;
  minimum_order: string;
  payment_terms: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

interface ProductSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProductSelect: (product: SupplierProduct) => void;
}

const ProductSearchModal: React.FC<ProductSearchModalProps> = ({
  isOpen,
  onClose,
  onProductSelect
}) => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<SupplierProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search all supplier products
  const searchProducts = async (query: string) => {
    if (!query.trim() || !token) return;

    setLoading(true);
    setError(null);

    try {
      // Get all suppliers first, then search their products
      const suppliersResponse = await fetch(API_ENDPOINTS.SUPPLIERS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!suppliersResponse.ok) {
        throw new Error(`HTTP error! status: ${suppliersResponse.status}`);
      }

      const suppliersResult = await suppliersResponse.json();
      if (!suppliersResult.success) {
        throw new Error(suppliersResult.error || 'Failed to fetch suppliers');
      }

      // Search products from all suppliers
      const allProducts: SupplierProduct[] = [];
      
      for (const supplier of suppliersResult.data) {
        try {
          const productsResponse = await fetch(API_ENDPOINTS.SUPPLIER_PRODUCTS(supplier._id), {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (productsResponse.ok) {
            const productsResult = await productsResponse.json();
            if (productsResult.success) {
              // Filter products by search query
              const filteredProducts = productsResult.data.filter((product: SupplierProduct) =>
                (product.product_name?.toLowerCase() || '').includes(query.toLowerCase()) ||
                (product.product_brand?.toLowerCase() || '').includes(query.toLowerCase()) ||
                (product.product_description?.toLowerCase() || '').includes(query.toLowerCase())
              );
              
              // Add supplier name to each product
              const productsWithSupplier = filteredProducts.map((product: SupplierProduct) => ({
                ...product,
                supplier_name: supplier.name
              }));
              
              allProducts.push(...productsWithSupplier);
            }
          }
        } catch (error) {
          console.error(`Error fetching products for supplier ${supplier._id}:`, error);
        }
      }

      // Remove duplicates based on specific_id
      const uniqueProducts = allProducts.filter((product, index, self) => 
        index === self.findIndex(p => p.specific_id === product.specific_id)
      );

      setProducts(uniqueProducts);
    } catch (error) {
      console.error('Error searching products:', error);
      setError(t('common.failed_search_products'));
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.trim()) {
      searchProducts(query);
    } else {
      setProducts([]);
    }
  };

  // Handle product selection
  const handleProductSelect = (product: SupplierProduct) => {
    onProductSelect(product);
    onClose();
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setProducts([]);
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content product-search-modal">
        <div className="modal-header">
          <h2>{t('common.search_all_products')}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="search-input-container">
            <input
              type="text"
              placeholder={t('common.search_products_placeholder')}
              value={searchQuery}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>

          {loading && (
            <div className="loading-message">
              {t('common.searching_products')}
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {!loading && !error && products.length === 0 && searchQuery.trim() && (
            <div className="no-results">
              {t('common.no_products_found', { query: searchQuery })}
            </div>
          )}

          {!loading && !error && products.length > 0 && (
            <div className="products-list">
              {products.map((product) => (
                <div
                  key={product._id}
                  className="product-item"
                  onClick={() => handleProductSelect(product)}
                >
                  <div className="product-info">
                    <h3>{product.product_name}</h3>
                    {product.product_brand && (
                      <p className="product-brand">{t('common.brand')}: {product.product_brand}</p>
                    )}
                    {product.product_description && (
                      <p className="product-description">{product.product_description}</p>
                    )}
                    <div className="product-details">
                      {product.price > 0 && (
                        <span className="product-price">
                          {product.price} {product.currency}
                        </span>
                      )}
                      {product.delivery_time && (
                        <span className="product-delivery">
                          {t('common.delivery')}: {product.delivery_time}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductSearchModal; 